// src/modules/leads/route-planner/routeUtils.js

/**
 * Sample points along a Google Maps polyline path at regular intervals.
 * @param {google.maps.LatLng[]} path - Array of LatLng points from decoded polyline
 * @param {number} intervalMeters - Distance between samples in meters (default ~1 mile)
 * @returns {google.maps.LatLng[]} Sampled points
 */
export function samplePolyline(path, intervalMeters = 1609) {
  if (!path || path.length === 0) return [];
  if (!window.google?.maps?.geometry) return path;

  const { computeDistanceBetween } = window.google.maps.geometry.spherical;
  const points = [path[0]];
  let accumulated = 0;

  for (let i = 1; i < path.length; i++) {
    const segDist = computeDistanceBetween(path[i - 1], path[i]);
    accumulated += segDist;

    if (accumulated >= intervalMeters) {
      points.push(path[i]);
      accumulated = 0;
    }
  }

  // Always include the last point
  if (points.length > 0 && points[points.length - 1] !== path[path.length - 1]) {
    points.push(path[path.length - 1]);
  }

  return points;
}

/**
 * Find leads that are within a given distance of the route polyline.
 * @param {google.maps.LatLng[]} polylinePath - Decoded overview polyline from Directions result
 * @param {Array} leads - Array of lead objects with latitude/longitude
 * @param {number} maxDistanceMiles - Maximum deviation distance in miles
 * @param {Set} excludeIds - Set of lead IDs already in the route
 * @param {Object} filters - Optional additional filters { status, dateFrom, dateTo }
 * @returns {Array} Leads within the specified distance of the route
 */
export function findLeadsAlongRoute(polylinePath, leads, maxDistanceMiles, excludeIds, filters = {}) {
  if (!polylinePath || !leads || !window.google?.maps?.geometry) return [];

  const { computeDistanceBetween } = window.google.maps.geometry.spherical;
  const sampledPoints = samplePolyline(polylinePath, 1609); // Sample every ~1 mile
  const maxDistanceMeters = maxDistanceMiles * 1609.34;

  return leads.filter(lead => {
    if (excludeIds.has(lead.id)) return false;
    if (!lead.latitude || !lead.longitude) return false;

    // Apply additional filters
    if (filters.status && filters.status !== 'all') {
      const statusName = lead.status?.status_name || lead.serviceRequests?.[0]?.LeadStatus?.status_name || 'No Status';
      if (statusName !== filters.status) return false;
    }

    if (filters.dateFrom) {
      const createdAt = new Date(lead.createdAt);
      if (createdAt < new Date(filters.dateFrom)) return false;
    }

    if (filters.dateTo) {
      const createdAt = new Date(lead.createdAt);
      if (createdAt > new Date(filters.dateTo)) return false;
    }

    const leadLatLng = new window.google.maps.LatLng(
      parseFloat(lead.latitude),
      parseFloat(lead.longitude)
    );

    // Check if lead is within max distance of any sampled route point
    for (let i = 0; i < sampledPoints.length; i++) {
      const dist = computeDistanceBetween(leadLatLng, sampledPoints[i]);
      if (dist <= maxDistanceMeters) return true;
    }

    return false;
  });
}

/**
 * Generate a Google Maps directions URL with all waypoints.
 * @param {{ lat: number, lng: number, address: string }} departure
 * @param {{ lat: number, lng: number, address: string }} destination
 * @param {Array} orderedStops - Array of lead objects in optimized order
 * @returns {string} Google Maps URL
 */
export function generateGoogleMapsUrl(departure, destination, orderedStops) {
  const origin = `${departure.lat},${departure.lng}`;
  const dest = `${destination.lat},${destination.lng}`;

  let url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}&travelmode=driving`;

  if (orderedStops.length > 0) {
    const waypoints = orderedStops
      .map(stop => `${stop.latitude},${stop.longitude}`)
      .join('|');
    url += `&waypoints=${encodeURIComponent(waypoints)}`;
  }

  return url;
}

/**
 * Extract total distance and duration from a Directions result.
 * @param {google.maps.DirectionsResult} result
 * @returns {{ totalDistance: string, totalDuration: string, legs: Array }}
 */
export function extractRouteInfo(result) {
  if (!result?.routes?.[0]?.legs) return null;

  const legs = result.routes[0].legs;
  let totalDistanceMeters = 0;
  let totalDurationSeconds = 0;

  const legDetails = legs.map((leg, index) => {
    totalDistanceMeters += leg.distance.value;
    totalDurationSeconds += leg.duration.value;
    return {
      index,
      distance: leg.distance.text,
      duration: leg.duration.text,
      startAddress: leg.start_address,
      endAddress: leg.end_address,
    };
  });

  const totalMiles = (totalDistanceMeters / 1609.34).toFixed(1);
  const totalHours = Math.floor(totalDurationSeconds / 3600);
  const totalMinutes = Math.round((totalDurationSeconds % 3600) / 60);

  let totalDuration;
  if (totalHours > 0) {
    totalDuration = `${totalHours}h ${totalMinutes}m`;
  } else {
    totalDuration = `${totalMinutes} min`;
  }

  return {
    totalDistance: `${totalMiles} mi`,
    totalDuration,
    totalDistanceMeters,
    totalDurationSeconds,
    legs: legDetails,
    stopCount: legs.length - 1, // Exclude the final leg (last waypoint → destination)
  };
}

/**
 * Create a colored SVG pin marker with a label.
 * @param {string} color - Hex color for the pin
 * @param {string} label - Text label (e.g., "A", "1", "X")
 * @returns {string} Data URI for the SVG
 */
export function createNumberedPin(color, label) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="44" viewBox="0 0 32 44">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 28 16 28s16-16 16-28C32 7.163 24.837 0 16 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
      <text x="16" y="21" text-anchor="middle" fill="#fff" font-size="13" font-weight="bold" font-family="Roboto, Arial, sans-serif">${label}</text>
    </svg>
  `;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
}

/**
 * Create a simple dot marker for unselected leads.
 * @param {string} color - Hex color
 * @param {number} opacity - 0 to 1
 * @returns {string} Data URI for the SVG
 */
export function createDotPin(color, opacity = 0.5) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="28" viewBox="0 0 20 28" opacity="${opacity}">
      <path d="M10 0C4.477 0 0 4.477 0 10c0 7.5 10 18 10 18s10-10.5 10-18C20 4.477 15.523 0 10 0z" fill="${color}" stroke="#fff" stroke-width="1"/>
      <circle cx="10" cy="10" r="4" fill="#fff"/>
    </svg>
  `;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
}

// Dark map styles (reused from LeadMapView)
export const darkMapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

// Marker color constants
export const MARKER_COLORS = {
  departure: '#3d5afe',
  destination: '#27ae60',
  waypoint: '#F69620',
  unselected: '#95a5a6',
  alongRoute: '#9b59b6',
};
